'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { PortalServiceSyncedItemsResponceDto } from '@nimbus/portal-service/lib/client/api';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { getMenuItemsByMenuTypesFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalId } from '@nx-frontend/shared/store/portal-app';
import { MenuItemType, MenuType } from '@nx-frontend/shared/types';

interface DeleteProjectionsSelectProps {
  menuItemId?: string;
  menuItemType?: MenuItemType;
  onChange: (value: DeleteProjectionOriginType) => void;
}

export enum DeleteProjectionOriginType {
  DELETE_LINKED = 'delete-linked',
  KEEP_LINKED = 'keep-linked',
}

export const DeleteProjectionsSelect: React.FC<DeleteProjectionsSelectProps> = ({
  onChange,
  menuItemId,
  menuItemType,
}) => {
  const [portalId] = useUnit([$portalId]);
  const [getMenuItemsByMenuTypes] = useEvent([getMenuItemsByMenuTypesFx]);
  const [activeTab, setActiveTab] = useState(DeleteProjectionOriginType.DELETE_LINKED);
  const [linkedPagesHidden, setLinkedPagesHidden] = useState(true);
  const [links, setLinks] = useState<string[]>([]);

  const type = useMemo(() => {
    if (!menuItemType) {
      return 'portal';
    } else if (menuItemType === MenuItemType.PORTAL_SECTION || menuItemType === MenuItemType.NOTES_FOLDER) {
      return 'folder';
    } else {
      return 'page';
    }
  }, [menuItemType]);

  const items = [
    {
      title:
        type === 'portal'
          ? 'Delete linked folders and pages'
          : type === 'folder'
          ? 'Delete linked folders'
          : 'Delete linked pages',
      type: DeleteProjectionOriginType.DELETE_LINKED,
    },
    {
      title:
        type === 'portal'
          ? 'Keep linked folders and pages'
          : type === 'folder'
          ? 'Keep linked folders'
          : `Keep linked pages`,
      type: DeleteProjectionOriginType.KEEP_LINKED,
    },
  ];

  useEffect(() => {
    (async () => {
      const location = window.location;
      if (menuItemId) {
        const result = await axiosClient
          .get<PortalServiceSyncedItemsResponceDto>(`${generalEnv.nextCommonApiUrl}/portal/synced-items`, {
            params: {
              portalId,
              originalGlobalId: menuItemId,
              entityType: 'menuItem',
            },
            withCredentials: true,
          })
          .then((res) => res.data)
          .catch(() => null);
        const preparedLinks =
          result?.syncedItems.map((item) => {
            return `${location.protocol}//${location.host}/portal/${portalId}/visual${item.path}`;
          }) ?? [];
        setLinks(preparedLinks);
      } else {
        const items = await getMenuItemsByMenuTypes({
          menuTypes: [MenuType.PORTAL_TOPBAR, MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER],
        });
        const origins = items.filter((item) => item.isOrigin === true);
        const preparedLinks =
          origins.map((item) => {
            return `${location.protocol}//${location.host}/portal/${portalId}/visual${item.url}`;
          }) ?? [];
        setLinks(preparedLinks);
      }
    })();
  }, [getMenuItemsByMenuTypes, menuItemId, portalId]);

  const changeTab = (tab: DeleteProjectionOriginType) => {
    setActiveTab(tab);
    onChange(tab);
  };

  return (
    <div>
      {type !== 'portal' && (
        <div>
          This {type} is used as a linked {type} on other portals.{' '}
        </div>
      )}
      {type === 'portal' && <div>This portal contains folders and pages which is linked with other portals. </div>}

      <div className="flex gap-3 mt-3">
        {items.map((item) => {
          return (
            <div
              key={item.type}
              className={classNames('flex flex-1 items-center h-12 gap-2 px-4 py-2 border rounded cursor-pointer', {
                'border-neutral-200': activeTab !== item.type,
                'border-cyan-900': activeTab === item.type,
              })}
              onClick={() => {
                changeTab(item.type);
              }}
            >
              <div className="whitespace-nowrap mr-auto text-xs">{item.title}</div>
            </div>
          );
        })}
      </div>
      <div className="py-2 px-3 border-l-2 border-red-500 bg-red-50 mt-3 rounded-r">
        {type !== 'portal' && (
          <div>
            {activeTab === DeleteProjectionOriginType.DELETE_LINKED && (
              <span>If you delete the {type}, it will also be removed from other portals.</span>
            )}
            {activeTab === DeleteProjectionOriginType.KEEP_LINKED && (
              <span>Only original {type} will be deleted, all linked will be unlinked and kept</span>
            )}
          </div>
        )}
        {type === 'portal' && (
          <div>
            {activeTab === DeleteProjectionOriginType.DELETE_LINKED && (
              <span>
                If you delete the folder, it and its linked pages will be removed from all linked portals as well.
              </span>
            )}
            {activeTab === DeleteProjectionOriginType.KEEP_LINKED && (
              <span>Only original folders and pages will be deleted, all linked will be unlinked and kept</span>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 min-w-[540px] max-w-full">
        <div className="flex justify-start">
          <div
            className="flex text-cyan-900 items-center gap-2 cursor-pointer text-xs"
            onClick={() => setLinkedPagesHidden(!linkedPagesHidden)}
          >
            <span className="font-bold">
              {linkedPagesHidden ? 'Show' : 'Hide'}{' '}
              {type === 'portal' ? 'objects' : type === 'folder' ? 'linked folders' : 'linked pages'} list
            </span>
            <Icon
              icon={IconsMap.ARROW_DOWN_2}
              className={classNames('w-4 h-4 text-cyan-900', {
                'rotate-180': !linkedPagesHidden,
              })}
            />
          </div>
        </div>
        {!linkedPagesHidden && (
          <div className="py-2 px-4 bg-zinc-50 mt-2 gap-2 flex flex-col min-w-96 rounded-md overflow-auto max-h-72">
            <div className="text-[10px] font-bold uppercase">
              {type === 'portal' ? 'Folders and pages' : type === 'folder' ? 'Folders affected' : 'Pages affected'}
            </div>
            {links.map((link) => {
              return (
                <div key={link} className="text-xs flex items-center justify-between gap-6">
                  <span>{link}</span>
                  <Link href={link} target={'_blank'}>
                    <Icon icon={IconsMap.OPEN} className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
